// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inviteFederatedUser, inviteUser as inviteLocalUser } from '../services/userService';
import { createRoleAssignment, getUserRoleAssignments } from '../services/roleAssignmentService';
import { useSharedSession } from '../auth/useSharedSession';
import { isAssignmentCurrent } from './useUserDetail';
import { ApiError } from '../../helios/services/api';
import type { AssignableScope } from './useAssignableRoles';
import type { UserWithOrg } from '../types/user';

/** Federated: signs in with their company account. Local: a Helios-only account. */
export type AccountMode = 'federated' | 'local';

export interface InviteUserInput {
  mode: AccountMode;
  email: string;
  /** Required for a local account (`UserInvite` needs both); optional for a
   *  federated one, whose name comes from their company account. */
  firstName: string;
  lastName: string;
  roleId: string;
  /** Raw IAM role name, for messages. */
  roleName: string;
  scope: AssignableScope;
  /** Regions or sites to grant at. Ignored for ORGANIZATION, which is the caller's org. */
  resourceIds: string[];
}

/** What adding the user did, when it went ahead (AB#35246 outcome rule). */
export interface InviteUserResult {
  /** `existing_federated`: the employee was already in the organization, and
   *  the role was granted to them — the retry path after a failed grant. */
  outcome: 'created' | 'existing_federated';
  mode: AccountMode;
  user: UserWithOrg;
}

/** Why adding the user was refused. Nothing was created and no role was granted. */
export type InviteRefusal =
  | 'existing_local'
  | 'duplicate_local'
  | 'domain_not_federated'
  | 'organization_mismatch';

const REFUSAL_BY_CODE: Record<string, InviteRefusal> = {
  LOCAL_ACCOUNT_EXISTS: 'existing_local',
  USER_ALREADY_EXISTS: 'duplicate_local',
  EMAIL_DOMAIN_NOT_FEDERATED: 'domain_not_federated',
  ORGANIZATION_MISMATCH: 'organization_mismatch',
};

/** The BFF refused to add the user; `existingUserId` is set when the email
 *  already belongs to someone the admin can be sent to. */
export class InviteRefusedError extends Error {
  reason: InviteRefusal;
  existingUserId?: string;

  constructor(reason: InviteRefusal, message: string, existingUserId?: string) {
    super(message);
    this.name = 'InviteRefusedError';
    this.reason = reason;
    this.existingUserId = existingUserId;
  }
}

/**
 * Thrown when the IAM user exists but granting the chosen role failed partway
 * through (ADO #31303: "surface the partial-failure state, not a silent
 * success"). Carries the user's id so the caller can send the admin to finish
 * the grant from the user's own detail page (ADO #22697).
 */
export class InvitePartialFailureError extends Error {
  userId: string;

  constructor(userId: string, message: string) {
    super(message);
    this.name = 'InvitePartialFailureError';
    this.userId = userId;
  }
}

export interface InviteUserView {
  invite: (input: InviteUserInput) => Promise<InviteUserResult>;
  isInviting: boolean;
}

function toRefusal(err: unknown): InviteRefusedError | null {
  if (!(err instanceof ApiError) || !err.code) return null;
  const reason = REFUSAL_BY_CODE[err.code];
  if (!reason) return null;
  const existing = err.details?.existing_user_id;
  return new InviteRefusedError(
    reason,
    err.message,
    typeof existing === 'string' ? existing : undefined
  );
}

/**
 * Add user (AB#35252): creates the IAM user as a federated employee or a local
 * account, then grants the chosen role at each chosen resource as its own
 * `POST /role-assignments` call (ADR-0026). Create and grant stay one action,
 * because a user with no role can do nothing.
 *
 * Only the create step differs between the two modes. A federated `existing`
 * still grants the role — repeating the add is how an admin retries after the
 * grant failed — skipping any grant the user already holds. Refusals reject
 * with `InviteRefusedError` before anything is granted; a failed grant after
 * the user exists rejects with `InvitePartialFailureError`.
 */
export function useInviteUser(): InviteUserView {
  const queryClient = useQueryClient();
  const { organizationId } = useSharedSession();

  const mutation = useMutation({
    mutationFn: async (input: InviteUserInput): Promise<InviteUserResult> => {
      const { mode, email, firstName, lastName, roleId, roleName, scope } = input;
      // Where the role goes is settled before anything is created, so a form
      // that can't be granted is refused with nothing created rather than
      // leaving a new user with no role.
      const resourceIds =
        scope === 'ORGANIZATION' ? (organizationId ? [organizationId] : []) : input.resourceIds;
      if (resourceIds.length === 0) {
        throw new Error(
          scope === 'ORGANIZATION'
            ? "Your organization hasn't loaded yet, so the role can't be granted. Try again in a moment."
            : `Choose at least one ${scope === 'REGION' ? 'region' : 'site'} for the ${roleName} role.`
        );
      }

      let user: UserWithOrg;
      let outcome: InviteUserResult['outcome'];
      try {
        if (mode === 'federated') {
          const displayName = `${firstName} ${lastName}`.trim();
          const response = await inviteFederatedUser({
            email,
            ...(displayName ? { displayName } : {}),
          });
          user = response.user;
          outcome = response.outcome === 'existing' ? 'existing_federated' : 'created';
        } else {
          user = await inviteLocalUser({ email, firstName, lastName });
          outcome = 'created';
        }
      } catch (err) {
        throw toRefusal(err) ?? err;
      }

      const who = firstName.trim() || email;

      let toGrant = resourceIds;
      if (outcome === 'existing_federated') {
        try {
          // Only a current grant counts as held: a deactivated or expired one
          // gives no access, so it is granted again.
          const held = (await getUserRoleAssignments(user.id)).filter(a => isAssignmentCurrent(a));
          toGrant = resourceIds.filter(
            id => !held.some(a => a.roleId === roleId && a.resourceId === id)
          );
        } catch {
          // Can't tell what they hold: grant everything and let IAM refuse duplicates.
        }
      }

      const results = await Promise.allSettled(
        toGrant.map(resourceId => createRoleAssignment({ userId: user.id, roleId, resourceId }))
      );
      const failedCount = results.filter(r => r.status === 'rejected').length;
      if (failedCount > 0) {
        const message =
          failedCount === toGrant.length
            ? `${who} was added, but the ${roleName} role couldn't be granted — finish setting it up from their profile.`
            : `${who} was added, but the ${roleName} role failed to grant at ${failedCount} of ${toGrant.length} places — finish setting it up from their profile.`;
        throw new InvitePartialFailureError(user.id, message);
      }

      return { outcome, mode, user };
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['admin-center', 'users'] }),
  });

  return {
    invite: input => mutation.mutateAsync(input),
    isInviting: mutation.isPending,
  };
}
